import { useQueries } from '@tanstack/react-query'

import { getProductStructure, productStructureKey, useCatalogProducts } from '@/features/roles'
import type { ProductStructure } from '@/features/roles'

export interface ProductCatalog {
  products: string[]
  structuresByProduct: Record<string, ProductStructure>
  isLoading: boolean
}

// Every catalogued product's full domain/resource/scope structure, loaded at
// once — unlike the tier-matrix role editor (which only ever works one
// product at a time via useProductStructure), the allowed-scopes ceiling
// editor offers every product's delegation scopes together, since a client's
// ceiling isn't scoped to a single product. Shares the same react-query cache
// entries as the roles feature's structure reads (productStructureKey), so
// switching between the role editor and the ceiling editor never refetches.
export function useProductCatalog(): ProductCatalog {
  const { data: products, isLoading: productsLoading } = useCatalogProducts()

  const structureQueries = useQueries({
    queries: (products ?? []).map((product) => ({
      queryKey: productStructureKey(product),
      queryFn: () => getProductStructure(product),
    })),
  })

  const structuresByProduct: Record<string, ProductStructure> = {}
  structureQueries.forEach((query, index) => {
    const product = products?.[index]
    if (product && query.data) {
      structuresByProduct[product] = query.data
    }
  })

  return {
    products: products ?? [],
    structuresByProduct,
    isLoading: productsLoading || structureQueries.some((query) => query.isLoading),
  }
}
